import React from "react";
import { useSelector } from "react-redux";
import styled from "styled-components";
import { palette, size } from "styled-theme";
import { animated } from "@react-spring/web";
import { formatMoney } from "@src/utils/helpers/stringHelper";
import CheckMark from "@theme/assets/checkMark.svg";

const ProgressionStepsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;


  @media only screen and (max-width: ${size("S")}) {
    flex-direction: column;
    position: absolute;
    justify-content: flex-start;
    top: 60px;
    background-color: ${palette("color", 31)};
    padding-right: 0px;
    border-radius: 0 0 15px 15px;
  }
`;

const FlexRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 36px;
  position: relative;

  > div {
    max-width: 150px;
    justify-content: center;
  }

  > div:nth-child(1) {
    max-width: 120px;
    justify-content: flex-start;
  }

  > div:last-child {
    justify-content: flex-end;
  }

  @media only screen and (max-width: ${size("ML")}) {
    justify-content: flex-end;
    margin-right: 90px;
    padding-right: 10px;
    border-right: 1px solid rgba(0, 0, 0, 0.08);

    > div {
      max-width: 50px;
      justify-content: center;
    }

    > div:nth-child(1) {
      max-width: 50px;
      justify-content: center;
    }

    > div:last-child {
      max-width: 50px;
      justify-content: center;
    }
  }

  @media only screen and (max-width: ${size("S")}) {
    justify-content: center;
    margin-right: 0;
    background-color: ${palette("color", 31)};
    gap: 10px;
    border-right: none;
    padding-right: 0px;
    border-radius: 0 0 10px 10px;

    > div {
      max-width: 100%;
      justify-content: center;
    }

    > div:nth-child(1) {
      max-width: 100%;
      justify-content: center;
      margin-left: 3em;
    }

    > div:last-child {
      max-width: 100%;
      justify-content: center;
    }
  }
`;

const StepCircle = styled(animated.div)`
  width: 26px;
  height: 26px;
  border-radius: 50%;
  border: 1px solid #00000014;
  background: ${palette("color", 16)};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  color: ${palette("color", 15)};
  position: relative;
  z-index: 1;

  div {
    justify-content: center;
  }

  @media only screen and (max-width: ${size("ML")}) {
    justify-content: center;
  }

  @media only screen and (max-width: ${size("S")}) {
    width: 100%;
    height: 5px;
    border-radius: 15px;
    border: none;

    span {
      display: none;
    }
  }
`;

const StepName = styled.span`
  margin-left: 10px;
  color: ${palette("color", 15)};
  font-size: 14px;
  opacity: 0.6;

  @media only screen and (max-width: ${size("ML")}) {
    display: none;
  }
`;

const CompletedStepCircle = styled.div`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: ${palette("color", 36)};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  color: ${palette("color", 30)};
  position: relative;
  z-index: 1;

  @media only screen and (max-width: ${size("S")}) {
    width: 100%;
    height: 5px;
    border-radius: 15px;
    background: ${palette("color", 30)};

    span {
      display: none;
    }
  }
`;

const CompletedStepName = styled.span`
  margin-left: 10px;
  color: ${palette("color", 15)};
  font-size: 14px;
  opacity: 0.8;

  @media only screen and (max-width: ${size("ML")}) {
    display: none;
  }
`;

const CurrentStepCircle = styled.div`
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: 2px solid ${palette("color", 30)};
  background: ${palette("color", 16)};
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  color: ${palette("color", 15)};
  position: relative;
  z-index: 1;

  @media only screen and (max-width: ${size("S")}) {
    width: 100%;
    height: 5px;
    border-radius: 15px;
    border: none;
    background: ${palette("color", 30)};

    span {
      display: none;
    }
  }
`;

const CurrentStepName = styled.span`
  margin-left: 10px;
  color: ${palette("color", 30)};
  font-size: 14px;
  font-weight: bold;

  @media only screen and (max-width: ${size("ML")}) {
    display: none;
  }
`;

const StepContainer = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  position: relative;
`;

const StepIndicator = styled.div`
  display: none;
  align-items: center;
  position: relative;
  color: ${palette("color", 15)};
  font-size: 12px;

  @media only screen and (max-width: ${size("S")}) {
    display: flex;
    margin-right: 3em;
    justify-content: center;
  }
`;

const LoanDetails = styled.div`
  display: none;
  color: ${palette("color", 15)};
  padding: 2px 0 12px;

  @media only screen and (max-width: ${size("S")}) {
    display: flex;
    align-self: flex-start;
    margin-left: 3em;
  }
`;

export const ProgressionBar = ({ percent, onClick, activeStep }) => {
  const steps = [
    { number: 1, name: "About" },
    { number: 2, name: "Living" },
    { number: 3, name: "Income" },
    { number: 4, name: "Statements" },
  ];

  const {
    initialValue: { requestedProductType, loanReason, loanAmount },
  } = useSelector((state) => state.loanForm || {});

  const requestedProductMapper = {
    PersonalLoan: "personal loan",
    LineOfCredit: "line of credit",
    CarLoan: "car loan",
    personalLoan: "personal loan",
    lineOfCredit: "line of credit",
    carLoan: "car loan",
  };

  const requestedLoanReasonMapper = {
    "Bond Loan": "bond loan",
    "Household Bills": "household bills",
    "Emergency Funds": "emergency funds",
    "Travel Holiday": "travel",
    "Vehicle Purchase": "vehicle purchase",
    "Consolidate Debt": "consolidate debt",
    "Home Renovations": "home renovations",
    "Furniture Appliances": "furniture appliances",
    "Funeral Expenses": "funeral expenses",
    "Wedding Expenses": "wedding expenses",
    "Medical/Dental Costs": "medical/dental costs",
    "Vet Costs": "vet costs",
    "Cosmetic Costs": "cosmetic costs",
    "Training For Me": "training",
    "Education For Children": "education",
    "Car Repairs": "car repairs"
  };

  const requestedLoanType = requestedProductMapper[requestedProductType];
  const requestedLoanReason = requestedLoanReasonMapper[loanReason];

  return (
    <ProgressionStepsWrapper onClick={onClick}>
      <FlexRow>
        {steps.map((step, index) => {
          const stepWidth = (index + 1) * 25;
          const isCompleted = percent >= stepWidth;
          const isCurrent = percent >= stepWidth - 25 && percent < stepWidth;

          return (
            <StepContainer key={step.number}>
              {isCompleted ? (
                <>
                  <CompletedStepCircle>
                    <span>
                        <img src={CheckMark} alt="green-checkmark" className="check-mark" />
                    </span>
                  </CompletedStepCircle>
                  <CompletedStepName>{step.name}</CompletedStepName>
                </>
              ) : isCurrent ? (
                <>
                  <CurrentStepCircle>
                    <span>{step.number}</span>
                  </CurrentStepCircle>
                  <CurrentStepName>{step.name}</CurrentStepName>
                </>
              ) : (
                <>
                  <StepCircle>
                    <span>{step.number}</span>
                  </StepCircle>
                  <StepName>{step.name}</StepName>
                </>
              )}
            </StepContainer>
          );
        })}
        <StepIndicator>
          <span>
            {activeStep} of {steps.length}
          </span>
        </StepIndicator>
      </FlexRow>
      {activeStep !== 1 && <LoanDetails><strong>{formatMoney(loanAmount)}</strong> &nbsp;{requestedLoanType} for {requestedLoanReason}</LoanDetails>}
    </ProgressionStepsWrapper>
  );
};
